/**
 * Testimonials from the shared Google Sheet (same file as Intelligence, Team and
 * the assessment leads) — tab `Testimonials`.
 *
 *   https://docs.google.com/spreadsheets/d/{SHEET_ID}/gviz/tq?tqx=out:csv&sheet=Testimonials
 *
 * Columns (row 1, exact names, any order):
 *   id | quote | author | role | company | industry | proof_stat | icon | related_case_id | published | order
 * See docs/TESTIMONIALS_SHEET_TEMPLATE.md.
 *
 * Safety: the gviz endpoint silently returns the FIRST tab when the named tab
 * does not exist, so a response without a `quote` column is treated as
 * "tab not set up yet" and the bundled testimonials are used instead.
 */
import { useState, useEffect } from 'react';
import { TESTIMONIALS } from '@/constants/testimonials';
import { tokenizeCSV } from '@/lib/csv';
import { softenDeep } from '@/lib/brandCopy';

const DEFAULT_SHEET_ID = '1HaZa-xdNleBwG6Hlxfp03jBmWUwQj5kv1PQCqFdS77g';
const SHEET_ID = process.env.REACT_APP_TEAM_SHEET_ID || DEFAULT_SHEET_ID;
const SHEET_TAB = process.env.REACT_APP_TESTIMONIALS_SHEET_TAB || 'Testimonials';
const SHEET_CSV_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:csv&sheet=${encodeURIComponent(SHEET_TAB)}`;

const CACHE_KEY = 'dd_testimonials_cache_v3';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

const truthy = (v) => /^(true|yes|1|y)$/i.test(String(v || '').trim());
// Sheet cells often arrive wrapped in stray straight/curly quotes — the card adds its own.
const unquote = (v) => String(v || '').trim().replace(/^[\s"“”'‘’]+/, '').replace(/[\s"“”'‘’]+$/, '').trim();

function normalizeRow(obj, idx) {
  const author = (obj.author || '').trim();
  const role = (obj.role || '').trim();
  const company = (obj.company || '').trim();
  const byline = [author, [role, company].filter(Boolean).join(', ')].filter(Boolean).join(' — ');
  const icon = /chart|growth|venture|fintech/i.test(obj.icon || '') ? 'chart' : 'shield';
  const order = Number.parseInt(obj.order, 10);
  return {
    id: (obj.id || `t${idx + 1}`).trim(),
    quote: unquote(obj.quote),
    author: byline || 'Client',
    authorName: author,
    role,
    company,
    industry: (obj.industry || '').trim(),
    proofStat: (obj.proof_stat || obj.proofstat || '').trim(),
    iconType: icon,
    relatedCaseId: (obj.related_case_id || '').trim() || null,
    published: obj.published === undefined || obj.published === '' ? true : truthy(obj.published),
    order: Number.isNaN(order) ? idx : order,
  };
}

/** @returns {Array|null} parsed rows, or null when the tab is missing / malformed */
export function parseTestimonials(csvText) {
  const rows = tokenizeCSV(csvText);
  if (!rows || rows.length < 2) return null;
  const header = rows[0].map((h) => String(h || '').trim().toLowerCase());
  if (!header.includes('quote')) return null; // not the Testimonials tab (gviz fell back to tab 1)
  const out = [];
  rows.slice(1).forEach((r, i) => {
    const obj = {};
    header.forEach((h, c) => { obj[h] = (r[c] || '').trim(); });
    if (!unquote(obj.quote)) return;
    const t = softenDeep(normalizeRow(obj, i));
    // Attribution rule (SEO/AEO audit): a testimonial needs at least a role or company
    // (ideally a named author) — fully anonymous quotes are not shown.
    const attributed = !!(t.authorName || t.role || t.company);
    if (t.published && attributed) out.push(t);
  });
  return out.length ? out.sort((a, b) => a.order - b.order) : null;
}

function getCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    return Date.now() - timestamp > CACHE_TTL ? null : data;
  } catch { return null; }
}
function setCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() })); } catch { /* quota */ }
}
export function clearTestimonialsCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

/** Bundled testimonials paint first; the sheet replaces them when it answers. */
export function useTestimonials() {
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);
  const [source, setSource] = useState('legacy');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const cached = getCache();
      if (cached) {
        if (!cancelled) { setTestimonials(cached); setSource('cache'); setLoading(false); }
        return;
      }
      try {
        const res = await fetch(SHEET_CSV_URL, { signal: AbortSignal.timeout(8000) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const parsed = parseTestimonials(await res.text());
        if (parsed && !cancelled) { setCache(parsed); setTestimonials(parsed); setSource('sheet'); }
      } catch (err) {
        console.warn('[TestimonialsSource] Sheet fetch failed, using bundled testimonials:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return { testimonials, source, loading };
}

export { SHEET_CSV_URL as TESTIMONIALS_SHEET_CSV_URL };
